import type { MembershipDto } from '@aawas/contracts';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  Bell,
  Building2,
  ClipboardList,
  DoorOpen,
  Home,
  Inbox,
  IndianRupee,
  LayoutDashboard,
  Megaphone,
  Menu,
  Settings,
  ShieldCheck,
  Ticket,
  UserCircle,
  Users,
  Wifi,
  WifiOff,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useNotifications, usePlatformSignups } from '../lib/queries';
import { useRealtime } from '../lib/realtime';
import { ROLE_LABEL, useSession } from '../lib/session';
import { Sheet } from '../ui';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

const NAV: Record<MembershipDto['role'], NavItem[]> = {
  owner: [
    { to: '/', label: 'Home', icon: Home, end: true },
    { to: '/visitors', label: 'Visitors', icon: DoorOpen },
    { to: '/complaints', label: 'Complaints', icon: Ticket },
    { to: '/notices', label: 'Notices', icon: Megaphone },
    { to: '/more', label: 'More', icon: Menu },
  ],
  tenant: [],
  family: [],
  guard: [
    { to: '/', label: 'Gate', icon: ShieldCheck, end: true },
    { to: '/gate/log', label: 'Log', icon: ClipboardList },
    { to: '/notices', label: 'Notices', icon: Megaphone },
    { to: '/more', label: 'More', icon: Menu },
  ],
  staff: [
    { to: '/', label: 'Tasks', icon: Wrench, end: true },
    { to: '/notices', label: 'Notices', icon: Megaphone },
    { to: '/more', label: 'More', icon: Menu },
  ],
  admin: [
    { to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
    { to: '/finance', label: 'Finance', icon: IndianRupee },
    { to: '/complaints', label: 'Complaints', icon: Ticket },
    { to: '/visitors', label: 'Visitors', icon: DoorOpen },
    { to: '/members', label: 'People', icon: Users },
    { to: '/notices', label: 'Notices', icon: Megaphone },
    { to: '/settings', label: 'Settings', icon: Settings },
  ],
};
NAV.tenant = NAV.owner;
NAV.family = NAV.owner;

/** Phone bottom bar: at most five slots, so the committee's less-used pages live under "More". */
const MOBILE_ADMIN: NavItem[] = [
  { to: '/', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/finance', label: 'Finance', icon: IndianRupee },
  { to: '/complaints', label: 'Complaints', icon: Ticket },
  { to: '/members', label: 'People', icon: Users },
  { to: '/more', label: 'More', icon: Menu },
];

export function Shell() {
  const { current, memberships, user, switchTo } = useSession();
  const { connected } = useRealtime();
  const notes = useNotifications();
  const qc = useQueryClient();
  const [picker, setPicker] = useState(false);
  const items = NAV[current.role];
  const mobileItems = current.role === 'admin' ? MOBILE_ADMIN : items;
  const unread = notes.data?.unreadCount ?? 0;
  // The operator's badge: societies waiting for a decision. Only fetched for the operator.
  const waitingSignups = usePlatformSignups('pending', user.platformAdmin).data?.length ?? 0;

  const choose = (m: MembershipDto) => {
    switchTo(m);
    qc.clear(); // never show one role's cached data under another
    setPicker(false);
  };

  return (
    <div className="flex min-h-dvh flex-col md:flex-row">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col border-r border-slate-200 bg-white md:flex">
        <div className="flex items-center gap-2 px-5 py-5">
          <Building2 className="h-6 w-6 text-brand-600" aria-hidden />
          <span className="text-lg font-bold">Aawas</span>
        </div>
        <button onClick={() => setPicker(true)} className="mx-3 mb-3 rounded-xl bg-slate-100 px-3 py-2.5 text-left hover:bg-slate-200">
          <div className="truncate text-sm font-semibold">{current.communityName}</div>
          <div className="text-xs text-slate-600">
            {ROLE_LABEL[current.role]}
            {current.unitLabel ? ` · ${current.unitLabel}` : ''}
          </div>
        </button>
        <nav className="flex-1 space-y-1 px-3">
          {items
            .filter((i) => i.to !== '/more')
            .map((i) => (
              <NavLink key={i.to} to={i.to} end={i.end} className={({ isActive }) => clsx('flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium', isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-700 hover:bg-slate-100')}>
                <i.icon className="h-5 w-5" aria-hidden />
                {i.label}
              </NavLink>
            ))}
          {user.platformAdmin && (
            <NavLink to="/platform" className={({ isActive }) => clsx('flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium', isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-700 hover:bg-slate-100')}>
              <Inbox className="h-5 w-5" aria-hidden /> Society requests
              {waitingSignups > 0 && <span className="ml-auto rounded-full bg-amber-500 px-2 py-0.5 text-xs font-bold text-white">{waitingSignups}</span>}
            </NavLink>
          )}
          <NavLink to="/notifications" className={({ isActive }) => clsx('flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium', isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-700 hover:bg-slate-100')}>
            <Bell className="h-5 w-5" aria-hidden /> Notifications
            {unread > 0 && <span className="ml-auto rounded-full bg-rose-600 px-2 py-0.5 text-xs font-bold text-white">{unread}</span>}
          </NavLink>
          <NavLink to="/me" className={({ isActive }) => clsx('flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium', isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-700 hover:bg-slate-100')}>
            <UserCircle className="h-5 w-5" aria-hidden /> {user.name ?? 'Profile'}
          </NavLink>
        </nav>
        <div className="flex items-center gap-2 px-5 py-4 text-xs text-slate-500">
          {connected ? <Wifi className="h-4 w-4 text-emerald-600" aria-hidden /> : <WifiOff className="h-4 w-4 text-amber-600" aria-hidden />}
          {connected ? 'Live' : 'Reconnecting'}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200 bg-white/95 px-4 backdrop-blur md:hidden" style={{ paddingTop: 'var(--safe-top)', minHeight: 'calc(3.5rem + var(--safe-top))' }}>
          <button onClick={() => setPicker(true)} className="min-w-0 flex-1 text-left">
            <div className="truncate text-sm font-bold leading-tight">{current.communityName}</div>
            <div className="text-xs text-slate-600">
              {ROLE_LABEL[current.role]}
              {current.unitLabel ? ` · ${current.unitLabel}` : ''}
              {memberships.length > 1 ? ' ▾' : ''}
            </div>
          </button>
          {!connected && <WifiOff className="h-5 w-5 text-amber-600" aria-label="Reconnecting" />}
          <NavLink to="/notifications" aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`} className="relative rounded-full p-2 hover:bg-slate-100">
            <Bell className="h-6 w-6" aria-hidden />
            {unread > 0 && <span className="absolute right-0.5 top-0.5 min-w-4 rounded-full bg-rose-600 px-1 text-center text-[10px] font-bold leading-4 text-white">{unread > 9 ? '9+' : unread}</span>}
          </NavLink>
        </header>

        <main className="mx-auto w-full max-w-3xl flex-1 px-4 pb-28 pt-4 md:pb-10 md:pt-8">
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        <nav className="fixed inset-x-0 bottom-0 z-30 grid border-t border-slate-200 bg-white/95 backdrop-blur md:hidden" style={{ gridTemplateColumns: `repeat(${mobileItems.length}, 1fr)`, paddingBottom: 'var(--safe-bottom)' }} aria-label="Main">
          {mobileItems.map((i) => (
            <NavLink key={i.to} to={i.to} end={i.end} className={({ isActive }) => clsx('flex min-h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-medium', isActive ? 'text-brand-700' : 'text-slate-600')}>
              <i.icon className="h-6 w-6" aria-hidden />
              {i.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <Sheet open={picker} onClose={() => setPicker(false)} title="Switch role">
        <div className="space-y-2">
          {memberships.map((m) => {
            const isCurrent = m.id === current.id;
            return (
              <button key={m.id} onClick={() => choose(m)} className={clsx('w-full rounded-2xl p-4 text-left ring-1', isCurrent ? 'bg-brand-50 ring-brand-500' : 'bg-white ring-slate-200 hover:bg-slate-50')}>
                <div className="font-semibold">{m.communityName}</div>
                <div className="text-sm text-slate-600">
                  {ROLE_LABEL[m.role]}
                  {m.title ? ` (${m.title})` : ''}
                  {m.unitLabel ? ` · Flat ${m.unitLabel}` : ''}
                </div>
              </button>
            );
          })}
        </div>
      </Sheet>
    </div>
  );
}
